import React, { useEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";
import { COLOR_GRAY_BANNER_BACKGROUND } from "../constants/general-constants";
import { TRUE } from "../constants/general-constants";
import { FALSE } from "../constants/general-constants";

am4core.addLicense(LICENCE_KEY);

export default function AmChartStackedEfficiency({
  chartId,
  categoryKey,
  chartData,
  chartKeys,
  height,
  width,
  showScrollbar,
  maxInitialPoints = 400,
  targetLines,
  hideLabels,
  legendPosition,
  yMax,
  leftAxisTitle = "Number of Observations",
  rightAxisTitle = "Utilisation (%)",
  isExpanded,
  disableHover = {FALSE}
}) {
  const ctxRef = useRef({
    chart: null,
    xAxis: null,
    yAxisLeft: null,
    yAxisRight: null,
    seriesById: {},
    rafId: null
  });

  const holderRef = useRef(null);
  const didInitRef = useRef(false);
  const targetArtifactsRef = useRef([]);

  useEffect(function init() {
    if (didInitRef.current) {
      return;
    }

    didInitRef.current = true;

    if (!holderRef.current) {
      return;
    }

    const chart = am4core.create(holderRef.current, am4charts.XYChart);
    ctxRef.current.chart = chart;

    chart.animationsEnabled = true;
    am4core.options.queue = false;
    chart.paddingLeft = 0;
    chart.paddingRight = 90;
    chart.fontSize = 11;

    // X Axis
    const xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxis.dataFields.category = categoryKey;
    xAxis.renderer.minGridDistance = 30;
    xAxis.renderer.labels.template.fill = am4core.color("#000");
    xAxis.renderer.labels.template.rotation = -90;
    xAxis.renderer.labels.template.horizontalCenter = "right";
    xAxis.renderer.labels.template.fontSize = 9;
    xAxis.renderer.cellStartLocation = 0.1;
    xAxis.renderer.cellEndLocation = 0.9;

    // Left Y Axis
    const yAxisLeft = chart.yAxes.push(new am4charts.ValueAxis());
    yAxisLeft.renderer.labels.template.fill = am4core.color("#000");
    yAxisLeft.title.text = leftAxisTitle || "";
    yAxisLeft.title.fill = am4core.color("#000");
    yAxisLeft.title.fontSize = 14;
    yAxisLeft.min = 0;
    yAxisLeft.renderer.minLabelPosition = 0;
    yAxisLeft.renderer.maxLabelPosition = 1;

    // Right Y Axis
    const yAxisRight = chart.yAxes.push(new am4charts.ValueAxis());
    yAxisRight.renderer.opposite = true;
    yAxisRight.renderer.labels.template.fill = am4core.color("#000");
    yAxisRight.title.text = "";
    yAxisRight.title.disabled = true;
    yAxisRight.min = 0;
    yAxisRight.max = 100;
    yAxisRight.strictMinMax = true;
    yAxisRight.renderer.minLabelPosition = 0;
    yAxisRight.renderer.maxLabelPosition = 1;
    yAxisRight.renderer.labels.template.maxWidth = 30;
    yAxisRight.renderer.labels.template.truncate = false;
    yAxisRight.renderer.labels.template.wrap = false;
    yAxisRight.renderer.labels.template.disabled = true;

    chart.rightAxesContainer.width = 40;
    chart.rightAxesContainer.paddingRight = 0;
    chart.rightAxesContainer.layout = "vertical";

    chart.numberFormatter.numberFormat = "#.##a";
    chart.numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: "K" },
      { number: 1e6, suffix: "M" },
      { number: 1e9, suffix: "Bn" }
    ];

    ctxRef.current.xAxis = xAxis;
    ctxRef.current.yAxisLeft = yAxisLeft;
    ctxRef.current.yAxisRight = yAxisRight;
    ctxRef.current.seriesById = {};

    return function cleanup() {
      try {
        if (targetArtifactsRef.current && targetArtifactsRef.current.length) {
          targetArtifactsRef.current.forEach(function (t) {
            try {
              if (t.label && !t.label.isDisposed()) {
                t.label.dispose();
              }
            } catch (e) {}

            try {
              if (t.range && !t.range.isDisposed()) {
                t.range.dispose();
              }
            } catch (e) {}
          });
        }
      } catch (e) {}

      targetArtifactsRef.current = [];

      if (ctxRef.current.rafId != null) {
        try {
          cancelAnimationFrame(ctxRef.current.rafId);
        } catch (e) {}
        ctxRef.current.rafId = null;
      }

      try {
        chart.dispose();
      } catch (e) {}

      ctxRef.current = {
        chart: null,
        xAxis: null,
        yAxisLeft: null,
        yAxisRight: null,
        seriesById: {},
        rafId: null
      };

      didInitRef.current = false;
    };
  }, [categoryKey, leftAxisTitle]);

  useEffect(function () {
    const xAxis = ctxRef.current.xAxis;
    const chart = ctxRef.current.chart;

    if (!xAxis || xAxis.isDisposed() || !chart) {
      return;
    }

    if (hideLabels && hideLabels === TRUE) {
      xAxis.renderer.labels.template.disabled = true;
      xAxis.renderer.minLabelPosition = 0;
      xAxis.renderer.maxLabelPosition = 1;
      xAxis.renderer.height = 0;
      chart.paddingBottom = 10;
    } else {
      xAxis.renderer.labels.template.disabled = false;
      xAxis.renderer.height = undefined;
    }
  }, [hideLabels]);

  useEffect(function () {
    const chart = ctxRef.current.chart;

    if (!chart || chart.isDisposed()) {
      return;
    }

    if (chart.scrollbarX && !showScrollbar) {
      try {
        chart.scrollbarX.dispose();
      } catch (e) {}
      chart.scrollbarX = null;
    } else if (!chart.scrollbarX && showScrollbar) {
      chart.scrollbarX = new am4core.Scrollbar();
    }
  }, [showScrollbar]);

  useEffect(function () {
    const chart = ctxRef.current.chart;

    if (!chart || chart.isDisposed()) {
      return;
    }

    if (legendPosition) {
      if (!chart.legend) {
        chart.legend = new am4charts.Legend();
      }

      chart.legend.position = legendPosition;
      chart.legend.markers.template.width = 10;
      chart.legend.markers.template.height = 10;
      chart.legend.markers.template.disposeChildren();

      const marker = chart.legend.markers.template.createChild(am4core.Circle);
      marker.width = 10;
      marker.height = 10;

      chart.legend.itemContainers.template.paddingTop = 2;
      chart.legend.itemContainers.template.paddingBottom = 2;
      chart.legend.itemContainers.template.paddingLeft = 5;
      chart.legend.itemContainers.template.paddingRight = 5;
      chart.legend.labels.template.fontSize = 12;
      chart.legend.valueLabels.template.fontSize = 12;
      chart.legend.maxHeight = 50;
      chart.legend.scrollable = true;
    } else {
      chart.legend = undefined;
    }
  }, [legendPosition]);

  useEffect(function () {
    const ctx = ctxRef.current;
    const chart = ctx.chart;
    const yAxisLeft = ctx.yAxisLeft;
    const yAxisRight = ctx.yAxisRight;

    if (!chart || chart.isDisposed() || !Array.isArray(chartKeys) || !yAxisLeft || !yAxisRight) {
      return;
    }

    const byId = ctx.seriesById || {};

    chartKeys.forEach(function (k) {
      let series = byId[k.id];
      const isUtilisation = k.id === "utilisation";

      if (!series) {
        if (isUtilisation) {
          series = chart.series.push(new am4charts.LineSeries());
          byId[k.id] = series;
          series.dataFields.categoryX = categoryKey;
          series.dataFields.valueY = k.id;
          series.yAxis = yAxisRight;
          series.stacked = false;
          series.strokeWidth = 2;
          series.tensionX = 0.8;
          series.columns.template.tooltipText = `[bold]{categoryX}[/]\n${k.title}: {valueY.value}`;

          const bullet = series.bullets.push(new am4charts.CircleBullet());
          bullet.circle.radius = 2.5;
		  if (disableHover === TRUE) {

	  		  // 🔹 Kill tooltips
	  		  series.tooltip.disabled = true;
	  		  series.columns.template.tooltipText = "";
	  		  series.tooltipText = "";

	  		  // 🔹 Kill hover states on columns
	  		  if (series.columns && series.columns.template) {
				let hoverState = series.columns.template.states.getKey("hover");

				if (!hoverState) {
				  hoverState = series.columns.template.states.create("hover");
				}

				hoverState.properties.fillOpacity = 1;
				hoverState.properties.strokeOpacity = 1;
	  		  }

	  		}
        } else {
          series = chart.series.push(new am4charts.ColumnSeries());
          byId[k.id] = series;
          series.stacked = true;
          series.dataFields.categoryX = categoryKey;
          series.dataFields.valueY = k.id;
          series.yAxis = yAxisLeft;
          series.columns.template.width = am4core.percent(100);
          series.columns.template.strokeOpacity = 1;
          series.columns.template.strokeWidth = 1;
          series.columns.template.fillOpacity = 1;
          series.sequencedInterpolation = false;
          series.interpolationDuration = 500;
          series.interpolationEasing = am4core.ease.quadOut;
          series.columns.template.tooltipText = `[bold]{categoryX}[/]\n${k.title}: {valueY.value}`;
		  if (disableHover === TRUE) {

		  	  		  // 🔹 Kill tooltips
		  	  		  series.tooltip.disabled = true;
		  	  		  series.columns.template.tooltipText = "";
		  	  		  series.tooltipText = "";

		  	  		  // 🔹 Kill hover states on columns
		  	  		  if (series.columns && series.columns.template) {
		  				let hoverState = series.columns.template.states.getKey("hover");

		  				if (!hoverState) {
		  				  hoverState = series.columns.template.states.create("hover");
		  				}

		  				hoverState.properties.fillOpacity = 1;
		  				hoverState.properties.strokeOpacity = 1;
		  	  		  }

		  	  		}
        }
		
      }

      series.name = k.title;

      if (k.color) {
        const col = am4core.color(k.color);

        if (series instanceof am4charts.ColumnSeries) {
          series.columns.template.fill = col;
          series.columns.template.stroke = col;
        } else {
          series.stroke = col;
          series.fill = col;
        }
      }
    });
  }, [chartKeys, categoryKey]);

  // Target lines on right axis
  useEffect(function () {
    const chart = ctxRef.current.chart;
    const yAxisRight = ctxRef.current.yAxisRight;

    if (!chart || chart.isDisposed() || !yAxisRight || yAxisRight.isDisposed()) {
      return;
    }

    try {
      if (targetArtifactsRef.current && targetArtifactsRef.current.length) {
        targetArtifactsRef.current.forEach(function (t) {
          try {
            if (t.label && !t.label.isDisposed()) {
              t.label.dispose();
            }
          } catch (e) {}

          try {
            if (t.range && !t.range.isDisposed()) {
              t.range.dispose();
            }
          } catch (e) {}
        });
      }
    } catch (e) {}

    targetArtifactsRef.current = [];

    try {
      if (yAxisRight.axisRanges) {
        yAxisRight.axisRanges.clear();
      }
    } catch (e) {}

    if (!Array.isArray(targetLines) || targetLines.length === 0) {
      chart.paddingRight = 70;
      return;
    }

    chart.paddingRight = isExpanded ? 90 : 70;

    targetLines.forEach(function (t) {
      if (!t || typeof t.value !== "number") {
        return;
      }

      var v = t.value;

      if (v < 0) {
        v = 0;
      }
      if (v > 100) {
        v = 100;
      }

      const range = yAxisRight.axisRanges.create();
      range.value = v;
      range.grid.stroke = am4core.color(COLOR_GRAY_BANNER_BACKGROUND);
      range.grid.strokeWidth = 1;
      range.grid.strokeOpacity = 1;
      range.grid.above = true;

      const label = range.label;
      label.text = t.title != null ? t.title : String(v);
      label.fontSize = 12;
      label.fill = am4core.color("#ffffff");
      label.padding(4, 10, 4, 10);
      label.isMeasured = false;
      label.horizontalCenter = "right";
      label.verticalCenter = "middle";
      label.inside = true;
      label.dx = isExpanded ? 100 : 80;
      label.dy = 0;
      label.zIndex = 1000;

      label.background = new am4core.RoundedRectangle();
      label.background.fill = am4core.color("#1f2937");
      label.background.fillOpacity = 1;
      label.background.stroke = am4core.color("rgba(255,255,255,0.15)");
      label.background.strokeWidth = 1;
      label.background.cornerRadius(5, 5, 5, 5);

      const shadow = label.filters.push(new am4core.DropShadowFilter());
      shadow.blur = 4;
      shadow.opacity = 0.25;
      shadow.color = am4core.color("#4BA3FF");

      targetArtifactsRef.current.push({
        label: label,
        range: range
      });
    });

    try {
      chart.invalidateRawData();
      chart.validateLayout();
    } catch (e) {}
  }, [targetLines, isExpanded]);

  useEffect(function () {
    const yAxisLeft = ctxRef.current.yAxisLeft;
    const chart = ctxRef.current.chart;

    if (!chart || chart.isDisposed() || !yAxisLeft || yAxisLeft.isDisposed()) {
      return;
    }

    if (yMax && typeof yMax === "number") {
      yAxisLeft.max = Math.ceil(yMax * 1.1);
      yAxisLeft.strictMinMax = true;

      try {
        chart.validateLayout();
      } catch (e) {
        console.warn("Chart validateLayout failed:", e);
      }
    } else {
      yAxisLeft.max = undefined;
      yAxisLeft.strictMinMax = false;
    }
  }, [yMax]);

  useEffect(function () {
    const ctx = ctxRef.current;
    const chart = ctx.chart;
    const xAxis = ctx.xAxis;

    if (!chart || chart.isDisposed() || !xAxis) {
      return;
    }

    const keys = Array.isArray(chartKeys)
      ? chartKeys.map(function (k) {
          return k.id;
        })
      : [];

    const dataIn = Array.isArray(chartData) ? chartData : [];

    if (ctx.rafId != null) {
      try {
        cancelAnimationFrame(ctx.rafId);
      } catch (e) {}
      ctx.rafId = null;
    }

    if (dataIn.length === 0) {
      chart.data = [];
      chart.invalidateRawData();
      return;
    }

    const clean = dataIn.map(function (row, idx) {
      const out = Object.assign({}, row);

      if (out[categoryKey] == null || out[categoryKey] === "") {
        out[categoryKey] = out.title || out.name || "cat_" + idx;
      }

      keys.forEach(function (k) {
        const v = out[k];
        out[k] = typeof v === "number" ? v : v == null ? 0 : Number(v);

        if (Number.isNaN(out[k])) {
          out[k] = 0;
        }
      });

      if (typeof out.utilisation === "number") {
        if (out.utilisation < 0) {
          out.utilisation = 0;
        }
        if (out.utilisation > 100) {
          out.utilisation = 100;
        }
      }

      return out;
    });

    const total = clean.length;
    const bigSet = total > 800;

    xAxis.renderer.labels.template.disabled = !!hideLabels || bigSet;
    xAxis.renderer.grid.template.disabled = bigSet;

    const initial = total > maxInitialPoints ? clean.slice(0, maxInitialPoints) : clean;

    chart.data = initial;
    chart.invalidateData();

    if (total > maxInitialPoints) {
      ctx.rafId = requestAnimationFrame(function () {
        if (!ctxRef.current.chart || chart.isDisposed()) {
          return;
        }

        chart.data = clean;
        chart.invalidateData();
        ctx.rafId = null;
      });
    }
  }, [chartId, chartData, chartKeys, categoryKey, maxInitialPoints, hideLabels]);

  return (
    <div
      id={chartId}
      ref={holderRef}
      style={{
        width: width,
        height: height
      }}
    />
  );
}